import { Location } from '@/Stores/UserStore';
import { RootState, typedStore,  } from '@/store';
import vatsApi from '@/Shared/VatsAPI';
import { SearchParams } from '@/Appeals/Citation/SearchParams';
import { SearchResults } from '@/Appeals/Citation/SearchResults';
import { Logger } from '@/Shared/Logger';
import i18n from '@/internationalization';
import { markModule } from './StoreTypes';
import { Dictionary } from 'vue-router/types/router';
import { Photos } from '@/Appeals/Photos';
import { Appellant } from '@/Appeals/AppellantInformation/Appellant';
import Customer from '@/Customer/Customer';
import { AppealInfo, PrepareAppeal } from '@/Appeals/Information/AppealInfo';
import { Appeal } from '@/Appeals/Information/Appeal';
import { CitationInfo } from '@/Appeals/Information/CitationInfo';
import { HearingInfo, PrepareHearing } from '@/Appeals/Information/HearingInfo';
import { Hearing } from '@/Appeals/Information/Hearing';
import { PreviousCitations } from '@/Appeals/PreviousCitations';
import { AppealResult } from '@/Appeals/AppealResult';
import { Note, PrepareNotes } from '@/Appeals/Notes/Note';
import { NoteType } from '@/Appeals/Notes/NoteType';
import { HearingResult } from '@/Appeals/HearingResult';
import { AppealReason } from '@/Appeals/AppealReason';
import { HearingOfficer } from '@/Appeals/HearingOfficer';
import { CheckEmptyOrWhitespace } from '@/Shared/Validator';
import { NoteTypeLkup } from '@/Admin/NotificationManagement/NoteTypeLkup';

export interface SearchPayload {
    parameters: SearchParams;
    location: Location;
}

export interface PhotoPayload {
    photos: Photos[];
    item: Photos;
    location: Location;
}
export interface NotesPayload {
    parameters: Note;
    location: Location;
}

export interface CustomerPayload {
    customer: Customer;
    location: Location;
}

export interface CitationInfoPayload {
    citationInfo: CitationInfo;
    location: Location;
}

export class AppealHearingParams {
    public appeal: Appeal | null = null;
    public hearing: Hearing | null = null;
    public citationNumber: string = '';
    public action: string | null = null;
    public vehicleLicense: string | null = null;
    public vehicleState: string | null = null;
    public userID: string | undefined = undefined;
}

export interface AppealHearingPayload {
    parameters: AppealHearingParams;
    location: Location;
}

export interface AppellantPayload {
    appellant: Appellant;
    location: Location;
}

export interface AppealState {
    searchParams: Dictionary<SearchParams>;
    searchResults: Dictionary<SearchResults>;
    photos: Dictionary<Photos[]>;
    notes: Dictionary<Note[]>;
    noteTypeList: Dictionary<NoteType[]>;
    previousList: Dictionary<PreviousCitations[]>;
    appealResultList: Dictionary<AppealResult[]>;
    hearingResultList: Dictionary<HearingResult[]>;
    appealReasonList: Dictionary<AppealReason[]>;
    hearingOfficerList: Dictionary<HearingOfficer[]>;
    customer: Dictionary<Customer>;
    appellant: Dictionary<Appellant>;
    appealInfo: Dictionary<AppealInfo>;
    hearingInfo: Dictionary<HearingInfo>;
    searchError: string | null;
    searchInProgress: boolean;
    resultListLoaded: boolean;
    hearingOfficerListLoaded: boolean;
    reasonListLoaded: boolean;
    appealInfoLoaded: boolean;
    hearingInfoLoaded: boolean;
    photosLoaded: boolean;
    notesLoaded: boolean;
    noteTypesLoaded: boolean;
    customerLoaded: boolean;
    appellantLoaded: boolean;
    previousListLoaded: boolean;
    hearingResultListLoaded: boolean;
}

export const appealModule = markModule({
    namespaced: true,
    state: {
        searchParams: {},
        searchResults: {},
        photos: {},
        notes: {},
        noteTypeList: {},
        previousList: {},
        appealResultList: {},
        hearingResultList: {},
        appealReasonList: {},
        hearingOfficerList: {},
        customer: {},
        appellant: {},
        appealInfo: {},
        hearingInfo: {},
        searchError: null,
        searchInProgress: false,
        resultListLoaded: false,
        reasonListLoaded: false,
        hearingOfficerListLoaded: false,
        appealInfoLoaded: false,
        hearingInfoLoaded: false,
        photosLoaded: false,
        customerLoaded: false,
        appellantLoaded: false,
        previousListLoaded: false,
        notesLoaded: false,
        hearingResultListLoaded: false,
        noteTypesLoaded: false
    } as AppealState,
    mutations: {
        setSearchParams(state, payload: { params: SearchParams, location: Location }) {
            const { params, location } = payload;
            state.searchParams = Object.assign({}, state.searchParams, {
                [location.urlSegment]: params
            });
            state.searchError = null;
        },
        setSearchResults(state, payload: { results: SearchResults, location: Location }) {
            const { results, location } = payload;
            state.searchResults = Object.assign({}, state.searchResults, {
                [location.urlSegment]: results
            });
            state.searchError = null;
            state.searchInProgress = false;
        },
        setPhotos(state, payload: { results: Photos[], location: Location }) {
            const { results, location } = payload;
            state.photos = Object.assign({}, state.photos, {
                [location.urlSegment]: results
            });
            state.searchError = null;
            state.searchInProgress = false;
        },
        setNotes(state, payload: { results: Note[], location: Location }) {
            const { results, location } = payload;
            state.notes = Object.assign({}, state.notes, {
                [location.urlSegment]: results
            });
            state.searchError = null;
            state.searchInProgress = false;
        },
        setNoteTypeList(state, payload: { results: NoteTypeLkup[], location: string }) {
            const { results, location } = payload;
            state.noteTypeList = Object.assign({}, state.noteTypeList, {
                [location]: results
            });
            state.searchError = null;
            state.searchInProgress = false;
        },
        setPreviousList(state, payload: { results: PreviousCitations[], location: Location }) {
            const { results, location } = payload;
            state.previousList = Object.assign({}, state.previousList, {
                [location.urlSegment]: results
            });
            state.searchError = null;
            state.searchInProgress = false;
        },
        setAppealResultList(state, payload: { results: AppealResult[], location: string }) {
            const { results, location } = payload;
            state.appealResultList = Object.assign({}, state.appealResultList, {
                [location]: results
            });
            state.searchError = null;
            state.searchInProgress = false;
        },
        setHearingResultList(state, payload: { results: HearingResult[], location: string }) {
            const { results, location } = payload;
            state.hearingResultList = Object.assign({}, state.hearingResultList, {
                [location]: results
            });
            state.searchError = null;
            state.searchInProgress = false;
        },
        setAppealReasonList(state, payload: { results: AppealReason[], location: string }) {
            const { results, location } = payload;
            state.appealReasonList = Object.assign({}, state.appealReasonList, {
                [location]: results
            });
            state.searchError = null;
            state.searchInProgress = false;
        },
        setHearingOfficerList(state, payload: { results: HearingOfficer[], location: string }) {
            const { results, location } = payload;
            state.hearingOfficerList = Object.assign({}, state.hearingOfficerList, {
                [location]: results
            });
            state.searchError = null;
            state.searchInProgress = false;
        },
        setAppealInfo(state, payload: { results: AppealInfo, location: Location }) {
            const { results, location } = payload;

            state.appealInfo = Object.assign({}, state.appealInfo, {
                [location.urlSegment]: results
            });

            state.searchError = null;
            state.searchInProgress = false;
        },
        setHearingInfo(state, payload: { results: HearingInfo, location: Location }) {
            const { results, location } = payload;

            state.hearingInfo = Object.assign({}, state.hearingInfo, {
                [location.urlSegment]: results
            });

            state.searchError = null;
            state.searchInProgress = false;
        },
        setCustomer(state, payload: { results: Customer, location: Location }) {
            const { results, location } = payload;
            state.customer = Object.assign({}, state.customer, {
                [location.urlSegment]: results
            });
            state.searchError = null;
            state.searchInProgress = false;
        },
        setAppellant(state, payload: { results: Appellant, location: Location }) {
            const { results, location } = payload;
            state.appellant = Object.assign({}, state.appellant, {
                [location.urlSegment]: results
            });
            state.searchError = null;
            state.searchInProgress = false;
        },
        setSearchError(state, error: string) {
            state.searchError = error;
            state.searchInProgress = false;
        },
        setSearchBegun(state) {
            state.searchInProgress = true;
        },
        setSearchEnd(state) {
            state.searchInProgress = false;
        },
        clearSearch(state) {
            state.photosLoaded = false;
            state.appealInfoLoaded = false;
            state.hearingInfoLoaded = false;
            state.customerLoaded = false;
            state.appellantLoaded = false;
            state.previousListLoaded = false;
            state.notesLoaded = false;
        }
    },
    getters: {
        searchParams(state, _, root) {
            if (root.currentLocation.locationId) {
                return state.searchParams[root.currentLocation.urlSegment] || new SearchParams();
            }
            return new SearchParams();
        },
        getSearchResults(state, _, root) {
            if (root.currentLocation.locationId) {
                return state.searchResults[root.currentLocation.urlSegment] || {};
            }
            return {};
        },
        getPhotos(state, _, root) {
            if (root.currentLocation.locationId) {
                return state.photos[root.currentLocation.urlSegment] || [];
            }
            return [];
        },
        getNotes(state, _, root) {
            if (root.currentLocation.locationId) {
                return state.notes[root.currentLocation.urlSegment] || [];
            }
            return [];
        },
        getPreviousList(state, _, root) {
            if (root.currentLocation.locationId) {
                return state.previousList[root.currentLocation.urlSegment] || [];
            }
            return [];
        },
        getAppealResultList(state, _, root): AppealResult[] {
            if (root.currentLocation.locationId) {
                return state.appealResultList[root.currentLocation.urlSegment] || [];
            }
            return [];
        },
        getHearingResultList(state, _, root): HearingResult[] {
            if (root.currentLocation.locationId) {
                return state.hearingResultList[root.currentLocation.urlSegment] || [];
            }
            return [];
        },
        getAppealReasonList(state, _, root) {
            if (root.currentLocation.locationId) {
                return state.appealReasonList[root.currentLocation.urlSegment] || [];
            }
            return [];
        },
        getNoteTypeList(state, _, root) {
            if (root.currentLocation.locationId) {
                return state.noteTypeList[root.currentLocation.urlSegment] || [];
            }
            return [];
        },
        getHearingOfficerList(state, _, root) {
            if (root.currentLocation.locationId) {
                return state.hearingOfficerList[root.currentLocation.urlSegment] || [];
            }
            return [];
        },
        getCustomer(state, _, root) {
            if (root.currentLocation.locationId) {
                return state.customer[root.currentLocation.urlSegment] || new Customer();
            }
            return new Customer();
        },
        getAppellant(state, _, root) {
            if (root.currentLocation.locationId) {
                return state.appellant[root.currentLocation.urlSegment] || new Appellant();
            }
            return new Appellant();
        },
        checkAppellantRequiredFields(state, _, root): boolean {
            if (root.currentLocation.locationId) {
                const app = state.appellant[root.currentLocation.urlSegment];
                if (app != null && app !== undefined
                    && Object.keys(app).length
                    && app.hasOwnProperty('firstName')
                    && app.hasOwnProperty('lastName')
                    && app.hasOwnProperty('address1')
                    && app.hasOwnProperty('city')
                    && app.hasOwnProperty('state')
                    && app.hasOwnProperty('zip')
                    && app.hasOwnProperty('email')
                    && !CheckEmptyOrWhitespace(app.firstName, 'string')
                    && !CheckEmptyOrWhitespace(app.lastName, 'string')
                    && !CheckEmptyOrWhitespace(app.address1, 'string')
                    && !CheckEmptyOrWhitespace(app.city, 'string')
                    && !CheckEmptyOrWhitespace(app.state, 'string')
                    && !CheckEmptyOrWhitespace(app.zip, 'string')
                    && !CheckEmptyOrWhitespace(app.email, 'string')) {
                        return true;
                    }
            }
            return false;
        },
        getAppealInfo(state, _, root) {
            if (root.currentLocation.locationId) {
                return state.appealInfo[root.currentLocation.urlSegment] || new AppealInfo();
            }
            return new AppealInfo();
        },
        getHearingInfo(state, _, root) {
            if (root.currentLocation.locationId) {
                return state.hearingInfo[root.currentLocation.urlSegment] || new HearingInfo();
            }
            return new HearingInfo();
        },
        searchError: (state) => state.searchError,
        searchInProgress: (state) => state.searchInProgress,
        appealInfoLoaded: (state) => state.appealInfoLoaded,
        hearingInfoLoaded: (state) => state.hearingInfoLoaded,
        photosLoaded: (state) => state.photosLoaded,
        customerLoaded: (state) => state.customerLoaded,
        appellantLoaded: (state) => state.appellantLoaded,
        previousListLoaded: (state) => state.previousListLoaded,
        notesLoaded: (state) => state.notesLoaded
    },
    actions: {
        async deletePhoto({ commit }, { photos, item, location }: PhotoPayload) {
            typedStore.modules.appealModule.mutations.setPhotos({
                results: photos,
                location
            });

            await vatsApi.delete<Photos[]>(`/${location.urlSegment}/citations/deletePhoto`, {
                data: item.citationPhoto
            });
        },
        async updateCustomer({ commit }, { customer, location }: CustomerPayload) {

            typedStore.modules.appealModule.mutations.setCustomer({
                results: customer,
                location
            });

            await vatsApi.post<Customer>(`/${location.urlSegment}/citations/customer/update`, customer, {
                headers: {
                    accept: 'application/json',
                }
            });
        },
        async updateCitation({commit}, { citationInfo, location }: CitationInfoPayload) {
            const response = await vatsApi.post<SearchResults>(`/${location.urlSegment}/citations/updatebyappeal`,
            citationInfo,
            {
                headers: {
                    accept: 'application/json',
                }
            });

            typedStore.modules.appealModule.mutations.setSearchResults({
                results: response.data,
                location
            });

        },
        async updateNote({commit}, { parameters, location }: NotesPayload) {
            await vatsApi.post<Note>(`/${location.urlSegment}/citations/notes/update`,
            parameters,
            {
                headers: {
                    accept: 'application/json',
                }
            });
        },
        async loadNoteTypeList({ commit }, location) {
            if (!typedStore.modules.appealModule.state.noteTypesLoaded) {
                typedStore.modules.appealModule.mutations.setSearchBegun(null);
                try {
                    typedStore.modules.appealModule.state.noteTypesLoaded = true;
                    const response = await vatsApi
                    .get<NoteTypeLkup[]>(`/${location}/notificationsadmin/GetNoteTypeLkups`, {
                        headers: {
                            accept: 'application/json',
                        }
                    });

                    typedStore.modules.appealModule.mutations.setNoteTypeList({
                        results: response.data,
                        location
                    });
                }
                catch (e) {
                    typedStore.modules.appealModule.state.noteTypesLoaded = false;
                    Logger.Log(e.message);
                    typedStore.modules.appealModule.mutations
                        .setSearchError(e.message || i18n.t('citation.search.results.defaultSearchError'));
                }
            }
        },
        async deleteNote({commit}, { parameters, location }: NotesPayload) {
            await vatsApi.delete<Note>(`/${location.urlSegment}/citations/notes/delete`,
            {
                data: parameters,
                headers: {
                    accept: 'application/json',
                }
            });
        },
        async updateAppeal({commit}, { parameters, location }: AppealHearingPayload) {
            const response = await vatsApi.post<AppealInfo>(`/${location.urlSegment}/citations/appeal/update`,
            parameters,
            {
                headers: {
                    accept: 'application/json',
                }
            });

            typedStore.modules.appealModule.mutations.setAppealInfo({
                results: PrepareAppeal(response.data),
                location
            });
        },
        async updateHearing({commit}, {parameters, location}: AppealHearingPayload) {
            const response = await vatsApi.post<HearingInfo>(`/${location.urlSegment}/citations/hearing/update`,
            parameters,
            {
                headers: {
                    accept: 'application/json',
                }
            });

            typedStore.modules.appealModule.mutations.setHearingInfo({
                results: PrepareHearing(response.data),
                location
            });
        },
        async updateAppellant({ commit }, { appellant, location }: AppellantPayload) {
            typedStore.modules.appealModule.mutations.setAppellant({
                results: appellant,
                location
            });

            await vatsApi.post<Appellant>(`/${location.urlSegment}/citations/appellant/update`, appellant, {
                headers: {
                    accept: 'application/json',
                }
            });
        },
        async beginSearch({ commit }, { parameters, location }: SearchPayload) {
            const nonDefaultParam = Object.keys(parameters)
                .find((key) => (parameters[key as keyof SearchParams]) ? true : false);
            if (!nonDefaultParam) {
                return;
            }
            typedStore.modules.appealModule.mutations.clearSearch(null);
            typedStore.modules.appealModule.mutations.setSearchBegun(null);

            typedStore.modules.appealModule.mutations.setSearchParams({
                params: parameters,
                location
            });

            try {
                const response = await vatsApi
                    .post<SearchResults>(`/${location.urlSegment}/citations/appealsearch`, parameters, {
                        headers: {
                            accept: 'application/json',
                        }
                    });

                typedStore.modules.appealModule.mutations.setSearchResults({
                    results: response.data,
                    location
                });

                const newParams = new SearchParams();
                newParams.citationNumber = parameters.citationNumber;
                newParams.vehicleLicense = response.data.vehicleLicense;
                newParams.vehicleState = response.data.vehicleState;

                typedStore.modules.appealModule.mutations.setSearchParams({
                    params: newParams,
                    location
                });
            }
            catch (e) {
                typedStore.modules.appealModule.mutations.clearSearch(null);
                Logger.Log(e.message);
                typedStore.modules.appealModule.mutations
                    .setSearchError(e.message || i18n.t('citation.search.results.defaultSearchError'));
            }
        },
        async searchPhotos({ commit }, { parameters, location }: SearchPayload) {
            typedStore.modules.appealModule.mutations.setSearchBegun(null);
            try {
                const responsePhotos = await vatsApi
                .post<Photos[]>(`/${location.urlSegment}/citations/photos`, parameters, {
                    headers: {
                        accept: 'application/json',
                    }
                });

                typedStore.modules.appealModule.mutations.setPhotos({
                    results: responsePhotos.data,
                    location
                });
                typedStore.modules.appealModule.state.photosLoaded = true;
            }
            catch (e) {
                typedStore.modules.appealModule.state.photosLoaded = false;
                Logger.Log(e.message);
                typedStore.modules.appealModule.mutations
                    .setSearchError(e.message || i18n.t('citation.search.results.defaultSearchError'));
            }
        },
        async loadNotes({ commit }, { parameters, location }: SearchPayload) {
            typedStore.modules.appealModule.mutations.setSearchBegun(null);
            try {
                const responseNotes = await vatsApi
                .post<Note[]>(`/${location.urlSegment}/citations/notes`, parameters, {
                    headers: {
                        accept: 'application/json',
                    }
                });

                typedStore.modules.appealModule.mutations.setNotes({
                    results: PrepareNotes(responseNotes.data),
                    location
                });
                typedStore.modules.appealModule.state.notesLoaded = true;
            }
            catch (e) {
                typedStore.modules.appealModule.state.notesLoaded = false;
                Logger.Log(e.message);
                typedStore.modules.appealModule.mutations
                    .setSearchError(e.message || i18n.t('citation.search.results.defaultSearchError'));
            }
        },
        async loadPreviousList({ commit }, { parameters, location }: SearchPayload) {
            typedStore.modules.appealModule.mutations.setSearchBegun(null);
            try {
                const responsePhotos = await vatsApi
                .post<PreviousCitations[]>(`/${location.urlSegment}/citations/previous`, parameters, {
                    headers: {
                        accept: 'application/json',
                    }
                });

                typedStore.modules.appealModule.mutations.setPreviousList({
                    results: responsePhotos.data,
                    location
                });
                typedStore.modules.appealModule.state.previousListLoaded = true;
            }
            catch (e) {
                typedStore.modules.appealModule.state.previousListLoaded = false;
                Logger.Log(e.message);
                typedStore.modules.appealModule.mutations
                    .setSearchError(e.message || i18n.t('citation.search.results.defaultSearchError'));
            }
        },
        async searchAppealInfo({ commit }, { parameters, location }: SearchPayload) {
            typedStore.modules.appealModule.mutations.setSearchBegun(null);
            try {
                const responseAppealInfo = await vatsApi
                .post<AppealInfo>(`/${location.urlSegment}/citations/appeal`, parameters, {
                    headers: {
                        accept: 'application/json',
                    }
                });

                typedStore.modules.appealModule.mutations.setAppealInfo({
                    results: PrepareAppeal(responseAppealInfo.data),
                    location
                });

                typedStore.modules.appealModule.state.appealInfoLoaded = true;
            }
            catch (e) {
                typedStore.modules.appealModule.state.appealInfoLoaded = false;
                Logger.Log(e.message);
                typedStore.modules.appealModule.mutations
                    .setSearchError(e.message || i18n.t('citation.search.results.defaultSearchError'));
            }
        },
        async loadHearingInfo({ commit }, { parameters, location }: SearchPayload) {
            typedStore.modules.appealModule.mutations.setSearchBegun(null);
            try {
                const responseAppealInfo = await vatsApi
                .post<HearingInfo>(`/${location.urlSegment}/citations/hearing`, parameters, {
                    headers: {
                        accept: 'application/json',
                    }
                });

                typedStore.modules.appealModule.mutations.setHearingInfo({
                    results: PrepareHearing(responseAppealInfo.data),
                    location
                });
                typedStore.modules.appealModule.state.hearingInfoLoaded = true;
            }
            catch (e) {
                typedStore.modules.appealModule.state.hearingInfoLoaded = false;
                Logger.Log(e.message);
                typedStore.modules.appealModule.mutations
                    .setSearchError(e.message || i18n.t('citation.search.results.defaultSearchError'));
            }
        },
        async loadAppealResultList({ commit }, location) {
            if (!typedStore.modules.appealModule.state.resultListLoaded) {
                typedStore.modules.appealModule.mutations.setSearchBegun(null);
                try {
                    typedStore.modules.appealModule.state.resultListLoaded = true;
                    const responseAppealResult = await vatsApi
                    .post<AppealResult[]>(`/${location}/citations/appealresultlist`, {
                        headers: {
                            accept: 'application/json',
                        }
                    });

                    typedStore.modules.appealModule.mutations.setAppealResultList({
                        results: responseAppealResult.data,
                        location
                    });
                }
                catch (e) {
                    typedStore.modules.appealModule.state.resultListLoaded = false;
                    Logger.Log(e.message);
                    typedStore.modules.appealModule.mutations
                        .setSearchError(e.message || i18n.t('citation.search.results.defaultSearchError'));
                }
            }
        },
        async loadHearingResultList({ commit }, location) {
            if (!typedStore.modules.appealModule.state.hearingResultListLoaded) {
                typedStore.modules.appealModule.mutations.setSearchBegun(null);
                try {
                    typedStore.modules.appealModule.state.hearingResultListLoaded = true;
                    const responseAppealResult = await vatsApi
                    .post<HearingResult[]>(`/${location}/citations/hearingresultlist`, {
                        headers: {
                            accept: 'application/json',
                        }
                    });

                    typedStore.modules.appealModule.mutations.setHearingResultList({
                        results: responseAppealResult.data,
                        location
                    });
                }
                catch (e) {
                    typedStore.modules.appealModule.state.hearingResultListLoaded = false;
                    Logger.Log(e.message);
                    typedStore.modules.appealModule.mutations
                        .setSearchError(e.message || i18n.t('citation.search.results.defaultSearchError'));
                }
            }
        },
        async loadHearingOfficerList({ commit }, location) {
            if (!typedStore.modules.appealModule.state.hearingOfficerListLoaded) {
                typedStore.modules.appealModule.mutations.setSearchBegun(null);
                try {
                    typedStore.modules.appealModule.state.hearingOfficerListLoaded = true;

                    const responseAppealResult = await vatsApi
                    .post<HearingOfficer[]>(`/${location}/citations/hearingofficers`, {
                        headers: {
                            accept: 'application/json',
                        }
                    });

                    typedStore.modules.appealModule.mutations.setHearingOfficerList({
                        results: responseAppealResult.data,
                        location
                    });
                }
                catch (e) {
                    typedStore.modules.appealModule.state.hearingOfficerListLoaded = false;
                    Logger.Log(e.message);
                    typedStore.modules.appealModule.mutations
                        .setSearchError(e.message || i18n.t('citation.search.results.defaultSearchError'));
                }
            }
        },
        async loadAppealReasonList({ commit }, location) {
            if (!typedStore.modules.appealModule.state.reasonListLoaded) {
                typedStore.modules.appealModule.state.reasonListLoaded = true;
                typedStore.modules.appealModule.mutations.setSearchBegun(null);
                try {
                    const responseAppealReason = await vatsApi
                    .post<AppealReason[]>(`/${location}/citations/appealreasonlist`, {
                        headers: {
                            accept: 'application/json',
                        }
                    });

                    typedStore.modules.appealModule.mutations.setAppealReasonList({
                        results: responseAppealReason.data,
                        location
                    });
                }
                catch (e) {
                    typedStore.modules.appealModule.state.reasonListLoaded = false;
                    Logger.Log(e.message);
                    typedStore.modules.appealModule.mutations
                        .setSearchError(e.message || i18n.t('citation.search.results.defaultSearchError'));
                }
            }
        },
        async searchCustomer({ commit }, { parameters, location }: SearchPayload) {
            if (!typedStore.modules.appealModule.getters.searchInProgress) {
                typedStore.modules.appealModule.mutations.setSearchBegun(null);
            }
            try {
                const responseCustomer = await vatsApi
                .post<Customer>(`/${location.urlSegment}/citations/customer`, parameters, {
                    headers: {
                        accept: 'application/json',
                    }
                });

                typedStore.modules.appealModule.mutations.setCustomer({
                    results: responseCustomer.data,
                    location
                });
                typedStore.modules.appealModule.state.customerLoaded = true;
            }
            catch (e) {
                typedStore.modules.appealModule.state.customerLoaded = false;
                Logger.Log(e.message);
                typedStore.modules.appealModule.mutations
                    .setSearchError(e.message || i18n.t('citation.search.results.defaultSearchError'));
            }
        },
        async searchAppellant({ commit }, { parameters, location }: SearchPayload) {
            if (!typedStore.modules.appealModule.getters.searchInProgress) {
                typedStore.modules.appealModule.mutations.setSearchBegun(null);
            }

            try {
                const responseAppellant = await vatsApi
                .post<Appellant>(`/${location.urlSegment}/citations/appellant`, parameters, {
                    headers: {
                        accept: 'application/json',
                    }
                });

                typedStore.modules.appealModule.mutations.setAppellant({
                    results: responseAppellant.data,
                    location
                });
                typedStore.modules.appealModule.state.appellantLoaded = true;
            }
            catch (e) {
                typedStore.modules.appealModule.state.appellantLoaded = false;
                Logger.Log(e.message);
                typedStore.modules.appealModule.mutations
                    .setSearchError(e.message || i18n.t('citation.search.results.defaultSearchError'));
            }
        }
    },
    modules: {}
}, {} as RootState);
